import { useCallback } from "react";

import toast from "react-hot-toast";
import { Trans } from "react-i18next";

import { CheckIcon, Trash2Icon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { userCache } from "utils/currentUser";
import util from "utils/utils";

import type {
  InvitationAcceptResponse,
  NotificationContentProps,
} from "../types";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

export const IdeaBusinessProfileInvitedActions = ({
  events = [],
  invitations = [],
  onAccept,
  onReject,
}: NotificationContentProps) => {
  const invitationId = events[0]?.invitation;
  const invitationExists = invitationId
    ? invitations.find((i) => i._id === invitationId && !i.acceptedAt)
    : undefined;

  const acceptInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.accept(
      invitationId,
      (data: InvitationAcceptResponse) => {
        if (data.permissions) {
          Object.entries(data.permissions).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              userCache.updatePermissions(key, value);
            }
          });
        }
        toast.success("Business profile joined");
        onAccept?.(invitationId);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onAccept]);

  const rejectInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.reject(
      invitationId,
      () => {
        onReject?.(invitationId);
        toast.success("Group invitation rejected");
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onReject]);

  if (!invitationExists) return null;
  return (
    <ButtonContainer>
      <Button
        color="green"
        size="small"
        onClick={acceptInvite}
        style={{ marginBottom: 5 }}
      >
        <Icon icon={CheckIcon} />
      </Button>
      <Button size="small" onClick={rejectInvite} variant="outline">
        <Icon icon={Trash2Icon} />
      </Button>
    </ButtonContainer>
  );
};

export const IdeaBusinessProfileInvited = ({
  events = [],
  ownerIdeaBusinessProfile,
}: NotificationContentProps) => {
  return (
    <Trans
      i18nKey="notifications.ideaBusinessProfileInvited"
      values={{
        name: util.getEventNames(events ?? []),
        profileName: ownerIdeaBusinessProfile?.name || "Deleted",
      }}
    />
  );
};
